import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { DEFAULT_HOME } from '@/auth/routes'
import { useBrand } from '@/brand/context'
import { useTenants } from '@/tenant/context'
import type { TenantMembership } from '@/tenant/context'
import { Lockup } from '@/shell/Lockup'
import { initials, slugify } from '@/lib/format'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/** The slug rule, mirrored from `CreateTenantRequest` so the message is immediate. */
const SLUG = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/

/**
 * Whether this navigation asked for the create form to be open on arrival.
 *
 * `useLocation().state` is `unknown` by construction — anything at all can push
 * anything at all onto the history entry — so it is narrowed here rather than
 * asserted at the call site. A wrong shape opens the list, which is what a
 * direct visit does anyway.
 */
function opensCreateForm(state: unknown): boolean {
  return typeof state === 'object' && state !== null && 'create' in state && state.create === true
}

/**
 * Pick an organisation, or make one.
 *
 * Outside the shell, because the shell's sidebar names the organisation you are
 * in and there is not one yet.
 *
 * **This stopped being the sign-in interstitial on 2026-09-10.** It used to be
 * where everybody with more than one organisation landed on every sign-in,
 * because `POST /auth/login` cannot know which one was meant and nothing else
 * answered the question. `POST /api/v1/tenants/select-default` answers it now,
 * so the audience here is down to two: a brand new account with no organisation
 * — which has nothing to select and something to *create* — and anyone who
 * arrives deliberately, from the switcher's *Manage organisations*, to see the
 * full list with roles and timezones or to make another one.
 *
 * `location.state.create` opens the form on arrival, which is how the
 * switcher's *New organisation* reaches it. Router state rather than a query
 * string: it is a one-shot instruction, and `?new=1` in the address bar would
 * re-open the form on every reload and every back-navigation to this page.
 *
 * **Since 2026-09-13 every organisation lives on its own host** (spec:
 * *Hosts*), and this is also what the apex shows a signed-in visitor in place
 * of the shell (`RequireTenantHost`). *Open* is therefore a handoff to that
 * organisation's host, not a switch of this session — except for the
 * organisation whose host this already is, which is marked *This workspace*
 * and opens in place.
 */
export function Organisations(): JSX.Element {
  const { memberships, status, failure, select, reload } = useTenants()
  const { signOut, user } = useAuth()
  const brand = useBrand()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const [creating, setCreating] = useState(() => opensCreateForm(location.state))

  // The organisation whose host this is, by its workspace address rather than
  // by the session: the session names it too on a tenant host, but the brand
  // is what the visitor can see in the address bar, and the two agree by
  // construction (`tenant_member` refuses a session bound elsewhere).
  const onThisHost = (row: TenantMembership): boolean =>
    brand.host === 'tenant' && row.tenant.slug === brand.workspace

  const switching = useMutation((row: { id: string; next?: string }) => selectAndReport(row))

  async function selectAndReport({ id, next }: { id: string; next?: string }) {
    const result = await select(id, next)
    if (result.ok) return { ok: true, data: id } as const
    // Only a genuine membership refusal gets the friendly sentence. Everything
    // else keeps the reason it actually failed for: a network drop, a 5xx, a
    // 429 and `TENANT_UNAVAILABLE` all used to arrive as "you were removed from
    // that organisation", which sends the user to ask an admin to re-invite
    // them for a problem that is going to clear on a retry (WEB-9). By code,
    // not by status: `WORKSPACE_UNVERIFIED` is a 403 too, and its remedy is
    // an inbox, not an administrator.
    const revoked = result.failure.code === 'NOT_A_MEMBER'
    return {
      ok: false,
      failure: revoked
        ? { ...result.failure, message: 'You are no longer a member of that organisation.' }
        : result.failure,
    } as const
  }

  return (
    <main className="app-public">
      <div className="ar-row" style={{ width: 'min(40rem, 100%)' }}>
        <span className="app-public__lockup">
          <Lockup />
        </span>
        <span className="app-spacer" />
        <ThemeToggle />
        <Button
          tone="quiet"
          size="sm"
          onClick={() => {
            void signOut()
          }}
        >
          Sign out
        </Button>
      </div>

      <section className="ar-card" style={{ width: 'min(40rem, 100%)' }}>
        <div className="ar-card__header">
          <h2>Your organisations</h2>
          <span className="ar-card__spacer" />
          {memberships.length > 0 && !creating ? (
            <Button
              tone="primary"
              size="sm"
              onClick={() => {
                setCreating(true)
              }}
            >
              New organisation
            </Button>
          ) : null}
        </div>

        {status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading your organisations" lines={2} />
          </div>
        ) : null}

        {status === 'error' && failure ? (
          <ErrorState
            failure={failure}
            title="Could not load your organisations"
            onRetry={reload}
          />
        ) : null}

        {status === 'ready' && memberships.length === 0 && !creating ? (
          <Empty
            icon="◫"
            title="No organisation yet"
            body={
              user?.email
                ? `Create one to start, or ask a teammate to invite ${user.email}. An invitation is addressed to your email, so it has to be sent to that exact address.`
                : 'Create one to start, or ask a teammate to send you an invitation.'
            }
            action={
              <Button
                tone="primary"
                onClick={() => {
                  setCreating(true)
                }}
              >
                Create an organisation
              </Button>
            }
          />
        ) : null}

        {status === 'ready' && memberships.length > 0 ? (
          <div className="ar-card__body ar-stack ar-stack--tight">
            <InlineError failure={switching.state.failure} />
            {memberships.map((row) => (
              <button
                key={row.tenant.id}
                type="button"
                className="app-tenant"
                disabled={switching.pending}
                onClick={() => {
                  // Here already: into the shell. Anywhere else: the handoff
                  // leaves this page, so there is nothing to navigate to after
                  // it resolves — the browser is on its way.
                  if (onThisHost(row)) {
                    void navigate(DEFAULT_HOME)
                    return
                  }
                  void switching.run({ id: row.tenant.id })
                }}
              >
                <span className="ar-avatar" aria-hidden="true">
                  {initials(row.tenant.name)}
                </span>
                <span className="app-tenant__body">
                  <strong>{row.tenant.name}</strong>
                  <span>
                    {row.membership.role} · {row.tenant.timezone}
                  </span>
                </span>
                <span className="ar-card__spacer" />
                {onThisHost(row) ? (
                  <span className="ar-badge ar-badge--success">This workspace</span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}

        {creating ? (
          <CreateOrganisation
            onCancel={() => {
              setCreating(false)
            }}
            onCreated={(row) => {
              toast.show({
                kind: 'success',
                title: `${row.tenant.name} is ready`,
                body: 'You are its owner.',
              })
              setCreating(false)
              // Into the wizard, not onto an empty dashboard: a brand-new
              // organisation has no site, no keywords and no voice, and every
              // screen the shell offers would be empty until it does. The
              // wizard is on the new organisation's own host, so it is a
              // handoff with `/onboarding` as the destination; if the host
              // will not take it yet, the row is in the list above with an
              // *Open* that says why.
              void switching.run({ id: row.tenant.id, next: '/onboarding' })
            }}
          />
        ) : null}
      </section>
    </main>
  )
}

/**
 * The create form.
 *
 * The slug is shown, pre-filled from the name and editable, rather than derived
 * silently: it is **immutable** once set (`update_tenant` accepts name and
 * timezone only), so a value the user never saw is one they cannot change
 * afterwards.
 */
function CreateOrganisation({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: (row: TenantMembership) => void
}): JSX.Element {
  const { adopt } = useTenants()
  const { refresh } = useAuth()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [touchedSlug, setTouchedSlug] = useState(false)

  const create = useMutation((body: { name: string; slug: string }) =>
    call(() => api.POST('/api/v1/tenants', { body })),
  )

  const effectiveSlug = touchedSlug ? slug : slugify(name)
  const slugError =
    effectiveSlug.length > 0 && !SLUG.test(effectiveSlug)
      ? 'Lowercase letters, numbers and hyphens; it cannot start or end with a hyphen.'
      : undefined

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (slugError) return
    const result = await create.run({ name: name.trim(), slug: effectiveSlug })
    if (!result.ok) return
    // Both halves, in the order the shell needs them. `adopt` puts the new
    // organisation in the list *now* rather than starting a refetch, and
    // `refresh` re-reads the session, which the server has already pointed at
    // it. Without the first, navigating to the shell finds a session naming an
    // organisation the list does not have yet and bounces straight back here —
    // which is exactly what the first live run did.
    const row = { tenant: result.data.tenant, membership: result.data.membership }
    adopt(row)
    await refresh()
    onCreated(row)
  }

  return (
    <form
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <div className="ar-card__body ar-stack">
        <InlineError failure={create.state.failure} />
        <TextField
          label="Organisation name"
          required
          autoFocus
          maxLength={120}
          help="What your team calls the business. You can change this later."
          value={name}
          onChange={(event) => {
            setName(event.target.value)
            create.reset()
          }}
        />
        <TextField
          label="Short name"
          required
          value={effectiveSlug}
          error={slugError}
          help="Used in links and reports. This one cannot be changed afterwards."
          onChange={(event) => {
            setTouchedSlug(true)
            setSlug(event.target.value)
            create.reset()
          }}
        />
      </div>
      <div className="ar-card__footer">
        <span className="ar-card__spacer" />
        <Button tone="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          tone="primary"
          type="submit"
          busy={create.pending}
          busyLabel="Creating"
          disabled={name.trim().length === 0 || effectiveSlug.length === 0 || Boolean(slugError)}
        >
          Create organisation
        </Button>
      </div>
    </form>
  )
}
